import { useCallback, useEffect, useRef } from 'react';
import {
  HeadingText,
  Modal,
  Button,
  StackLayout,
  Box,
  styled,
} from '@leagueplatform/genesis-core';
import { useForm, type FieldValues } from '@leagueplatform/web-common';
import {
  PRODUCT_AREA,
  trackAnalyticsEvent,
  EVENT_NAME,
  SCREEN_NAMES,
} from '@leagueplatform/analytics';
import { useIntl } from '@leagueplatform/locales';
import SignedDocumentIllustration from 'assets/signed-document-illustration.png';
import { ResponsiveButtonRow } from 'components/responsive-button-row/responsive-button-row.component';
import { useIsBelowTablet } from 'hooks/use-is-below-tablet/use-is-below-tablet.hooks';
import { TextCommunicationConsentText } from './text-communication-consent-text.component';

interface TextCommunicationModalFormValues extends FieldValues {
  acceptTextCommunicationTerms: boolean;
}

type TextCommunicationTermsModalProps = {
  open?: boolean;
  closeModal: (userAcceptedTermsAndConditions: boolean) => void;
  heading: string;
};

const ModalImage = styled(`img`, {
  alignSelf: 'center',
});

export const TextCommunicationTermsModal = ({
  open,
  closeModal,
  heading,
}: TextCommunicationTermsModalProps) => {
  const { handleSubmit } = useForm<TextCommunicationModalFormValues>({
    defaultValues: {
      acceptTextCommunicationTerms: false,
    },
  });
  const { formatMessage } = useIntl();

  // Used to prevent closeModal from being called twice due to the use of onOpenChange callback for other actions that would close the modal.
  const hasModalClosedWithoutAcceptance = useRef(false);

  const closeModalWithoutAcceptance = useCallback(() => {
    if (!hasModalClosedWithoutAcceptance.current) {
      hasModalClosedWithoutAcceptance.current = true;
      closeModal(false);
    }
  }, [closeModal]);

  const closeModalWithAcceptance = useCallback(() => {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      product_area: PRODUCT_AREA.SETTINGS,
      screen_name: SCREEN_NAMES.TEXT_TERMS_AND_CONDITIONS,
      detail: 'accept text terms',
    });
    closeModal(true);
  }, [closeModal]);

  const isBelowTablet = useIsBelowTablet();

  // Send screen loaded analytics event when the modal is opened.
  useEffect(() => {
    if (open) {
      trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
        product_area: PRODUCT_AREA.ONBOARDING,
        screen_name: SCREEN_NAMES.TEXT_TERMS_AND_CONDITIONS,
      });
    }
  }, [open]);

  return (
    <Modal.Root
      open={open}
      onOpenChange={(isOpen) => {
        // Handles if the user closes the menu via clicking outside of the modal or pressing the ESC key.
        if (!isOpen && hasModalClosedWithoutAcceptance.current === false)
          closeModalWithoutAcceptance();

        // Reset ref to default value, allowing the user to close the modal without acceptance multiple times.
        hasModalClosedWithoutAcceptance.current = false;
      }}
    >
      <Modal.Content
        showCloseButton={false}
        width={{
          '@initial': 639,
          '@mobile': '100%',
          '@mobileLandscape': '100%',
        }}
        data-testid="text-communication-modal"
      >
        <StackLayout spacing="$oneAndHalf" horizontalAlignment="stretch">
          <StackLayout orientation="horizontal" horizontalAlignment="end">
            <Modal.Close>
              <Button
                onClick={closeModalWithoutAcceptance}
                quiet
                priority="tertiary"
                icon="interfaceClose"
                size="medium"
                hideLabel
              >
                {formatMessage({ id: 'STR_CLOSE' })}
              </Button>
            </Modal.Close>
          </StackLayout>

          <ModalImage src={SignedDocumentIllustration} aria-hidden alt="" />
          <>
            <Box
              css={{
                marginBlockEnd: '$oneAndHalf',
              }}
            >
              <Modal.Title>
                <HeadingText
                  level="3"
                  size="lg"
                  css={{
                    marginBlockEnd: '$half',
                  }}
                >
                  {heading}
                </HeadingText>
              </Modal.Title>
              <TextCommunicationConsentText
                css={{ typography: '$buttonOne' }}
              />
            </Box>
            <StackLayout
              as="form"
              onSubmit={handleSubmit(closeModalWithAcceptance)}
            >
              <ResponsiveButtonRow
                primaryBtn={
                  <Button type="submit" width="fillContainer">
                    {formatMessage({ id: 'STR_ACCEPT_TERMS' })}
                  </Button>
                }
                secondaryBtn={
                  <Modal.Close>
                    <Button
                      onClick={() => {
                        trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                          product_area: PRODUCT_AREA.SETTINGS,
                          screen_name: SCREEN_NAMES.TEXT_TERMS_AND_CONDITIONS,
                          detail: 'reject text terms',
                        });

                        closeModalWithoutAcceptance();
                      }}
                      priority="secondary"
                      width="fillContainer"
                    >
                      {formatMessage({ id: 'STR_REJECT_TERMS' })}
                    </Button>
                  </Modal.Close>
                }
                css={{
                  paddingBlockEnd: isBelowTablet ? 56 : '$none',
                }}
              />
            </StackLayout>
          </>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
